import { hasAccessAtom, payableInvoiceCountAtom } from '@/lib/atoms/atoms';
import { Spinal } from '@/lib/components/core/spinal/spinal';
import { Heading } from '@/lib/components/core/typography/heading';
import { useRelationContext } from '@/lib/context';
import {
  useGetBankAccounts,
  useGetPaymentSettings,
  useGetPayments,
  useGetRenameableRelations,
} from '@/lib/queries';
import { useGetInPaymentInvoices } from '@/lib/queries/invoices/invoices';
import { PaymentInvoiceItem, PaymentProcessStatus } from '@/lib/types/payment';
import { Affix, Form } from 'antd';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { PaymentHeader } from './_components/InPaymentHeader';
import { InPaymentToolbar } from './_components/InPaymentToolbar';
import { PaymentInvoice } from './_components/paymentInvoice';
import { useInPaymentPageState } from './_hooks/useInPaymentPageState';
import styles from './styles.module.scss';

export const InPayment = () => {
  const { t } = useTranslation();
  const setPayableInvoiceCount = useSetAtom(payableInvoiceCountAtom);
  const hasAccess = useAtomValue(hasAccessAtom);
  const { isFrozen } = useRelationContext();
  const [form] = Form.useForm();

  const {
    data: invoices,
    refetch: refetchInvoices,
    isLoading: invoicesIsLoading,
  } = useGetInPaymentInvoices();
  const { data: paymentSettings, isLoading: paymentSettingsIsLoading } =
    useGetPaymentSettings();
  const { data: bankAccounts, isLoading: bankAccountsIsLoading } =
    useGetBankAccounts();
  const {
    data: paymentsInProgress,
    isLoading: paymentsIsLoading,
    isFetching: paymentsIsFetching,
    refetch: refetchPayments,
  } = useGetPayments({
    processStatus: PaymentProcessStatus.InProgress,
  });
  const { data: renameableRelationIds, isLoading: renameableIsLoading } =
    useGetRenameableRelations();

  const {
    checkedList,
    invoiceIds,
    errorMessages,
    noCheckedBoxes,
    isCheckAll,
    isIndeterminate,
    sepaGenerateLoading,
    showErrorMessages,
    unusedCreditInvoices,
    onCheckAllChange,
    onCheck,
    handleFieldsChange,
    handleSubmit,
    handleRemoveInvoice,
    handleRemoveCreditInvoice,
    handleSetIsInPaymentBatch,
    refreshPaymentsInProgress,
  } = useInPaymentPageState({
    form,
    t,
    bankAccounts,
    invoices,
    invoicesIsLoading,
    paymentSettings,
    paymentSettingsIsLoading,
    paymentsInProgress,
    refetchInvoices,
    refetchPayments,
    setPayableInvoiceCount,
  });

  const isLoading =
    invoicesIsLoading ||
    paymentSettingsIsLoading ||
    renameableIsLoading ||
    bankAccountsIsLoading ||
    paymentsIsLoading;

  const sepaInProgress = Boolean(
    paymentsInProgress?.length || sepaGenerateLoading,
  );

  return (
    <Spinal initializing={isLoading} loading={paymentsIsFetching}>
      <Heading text={t('payment.title')} />
      <Form form={form} layout="vertical" onFieldsChange={handleFieldsChange}>
        <Affix>
          <InPaymentToolbar
            bankAccounts={bankAccounts}
            checkedList={checkedList}
            errorMessages={errorMessages}
            hasAccess={hasAccess}
            isFrozen={isFrozen}
            noCheckedBoxes={noCheckedBoxes}
            sepaGenerateLoading={sepaGenerateLoading}
            sepaInProgress={sepaInProgress}
            showErrorMessages={showErrorMessages}
            unusedCreditInvoices={unusedCreditInvoices}
            onSubmit={handleSubmit}
            onRemoveCreditInvoice={(invoice) =>
              void handleRemoveCreditInvoice(0, invoice)
            }
            onRemoveInvoiceBatch={handleSetIsInPaymentBatch}
            onRefreshPaymentsInProgress={refreshPaymentsInProgress}
            selectPlaceholder={t('payment.ibanSelect.actions.select')}
            paymentInProgressText={t('api.payment.alreadyInProgress', {
              ns: 'errors',
            })}
            chooseBankAccountText={t('label.chooseBankAccount')}
            unusedCreditInvoiceText={t('payment.unusedCreditInvoice')}
            setInPaymentBatchText={t('payment.setInPaymentBatch', {
              totalCount: checkedList.length,
            })}
            refreshText={t('payment.action.refresh')}
            sepaButtonText={t('payment.sepa')}
          />
        </Affix>
        <div style={{ paddingTop: '10px' }}>
          <PaymentHeader
            isCheckAll={isCheckAll}
            isIndeterminate={isIndeterminate}
            onCheckAllChange={onCheckAllChange}
          />
        </div>

        <div className={styles.rowContainer}>
          <Form.List name="invoices">
            {(fields) => (
              <>
                {fields.map((field) => {
                  const paymentInvoice = form.getFieldValue([
                    'invoices',
                    field.name,
                  ]) as PaymentInvoiceItem | undefined;

                  if (!paymentInvoice?.invoice) {
                    return null;
                  }

                  return (
                    <div className={styles.invoiceRow} key={field.key}>
                      <PaymentInvoice
                        paymentInvoice={paymentInvoice}
                        invoiceIndex={field.name}
                        isChecked={checkedList.includes(invoiceIds[field.name])}
                        onCheck={onCheck}
                        renameableRelationIds={renameableRelationIds ?? []}
                        isDisabled={
                          hasAccess.isAccountManager ||
                          isFrozen ||
                          sepaInProgress
                        }
                        onRemoveInvoice={handleRemoveInvoice}
                        onRemoveCreditInvoice={handleRemoveCreditInvoice}
                      />
                    </div>
                  );
                })}
              </>
            )}
          </Form.List>
        </div>
      </Form>
    </Spinal>
  );
};
